// tslint:disable:space-in-parens max-line-length
import Vue from 'vue';
import Meta from 'vue-meta';
import Router, { Route, RouteConfig, RawLocation } from 'vue-router';

import { inExperimentGetter } from 'frontend/utils/experiment';
import { getWindowLocation } from 'frontend/utils/location';
import { getServerSideContext } from 'frontend/utils/ssr-context';

import About from 'frontend/views/about/About.vue';
import TrialCancellationPlanSurvey from 'frontend/views/premium/cancellation/TrialCancellationPlanSurvey.vue';
import TrialCancellationPlanStatus from 'frontend/views/premium/cancellation/TrialCancellationPlanStatus.vue';

function updateConfig(routes: RouteConfig[]): RouteConfig[] {
  return routes.map((route: RouteConfig): RouteConfig => {
    route.pathToRegexpOptions = { strict: true };
    if (route.children) {
      route.children = updateConfig(route.children);
    }
    return route;
  });
}

export default function createRouter(): Router {
  Vue.use(Router);
  Vue.use(Meta, {
    refreshOnceOnNavigation: true,
  });
  const router = new Router({
    mode: 'history',
    routes: updateConfig([
      {
        path: '/',
        name: 'homepage',
        component: () =>
          import(/* webpackChunkName: 'LoggedOutHomepage' */ './views/index/LoggedOutHomepage.vue'),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/account/',
        children: [
          {
            path: 'signup/',
            name: 'account_signup',
            meta: { redirect: true },
          },
          {
            path: 'login/',
            name: 'account_login',
            meta: { redirect: true },
          },
        ],
      },
      {
        path: '/account/settings/',
        name: 'accountsettings_nav',
        meta: { redirect: true },
      },
      {
        path: '/account/settings/data-portal/personal-data-export/',
        component: () =>
          import(
            /* webpackChunkName: 'PersonalDataExport' */ 'frontend/views/account/PersonalDataExport.vue'
          ),
        name: 'personal_data_export_page',
        meta: {},
      },
      {
        path: '/account/settings/data-portal/user-content-download/',
        component: () =>
          import(
            /* webpackChunkName: 'UserContentDownload' */ 'frontend/views/account/UserContentDownload.vue'
          ),
        name: 'user_content_download',
        meta: {},
      },
      {
        path: '/account/settings/perma_delete/',
        name: 'accountsettings_perma_delete',
        meta: { redirect: true },
      },
      {
        path: '/avatar/change/',
        name: 'avatar_change',
        meta: { redirect: true },
      },
      {
        path: '/profile/personal-information/',
        name: 'account_personalinfo',
        meta: { redirect: true },
      },
      {
        path: '/account/social/connections/',
        name: 'socialaccount_connections',
        meta: { redirect: true },
      },
      {
        path: '/account/password/change/',
        name: 'account_change_password',
        meta: { redirect: true },
      },
      {
        path: '/account/password/reset/',
        name: 'account_reset_password',
        meta: { redirect: true },
      },
      {
        path: '/account/email/',
        name: 'account_email',
        meta: { redirect: true },
      },
      {
        path: '/account/logout/',
        name: 'account_logout',
        meta: { redirect: true },
      },
      {
        path: '/profile/all-notification-preferences/',
        name: 'account_emailpreferences',
        meta: { redirect: true },
      },
      {
        path: '/profile/selecttimezone/',
        name: 'account_selecttimezone',
        meta: { redirect: true },
      },
      {
        path: '/premium/chargebee/return-page/',
        name: 'chargebee_return_page',
        component: () =>
          import(
            /* webpackChunkName: 'ChargebeeReturn' */ 'frontend/views/premium/checkout/ChargebeeReturn.vue'
          ),
        meta: {
          hideFooter: true,
        },
      },
      {
        path: '/premium/chargebee/process-dispute/',
        name: 'chargebee_process_dispute',
        component: () =>
          import(
            /* webpackChunkName: 'ChargebeeProcessDispute' */ 'frontend/views/premium/ChargebeeProcessDispute.vue'
          ),
        meta: {},
      },
      {
        path: '/payment/subscription_settings/',
        name: 'payment_subscription_settings',
        meta: { redirect: true },
      },
      {
        path: '/payment/subscription_settings/manage_group_plan/',
        component: () =>
          import(
            /* webpackChunkName: 'ManageGroupPlan' */ 'frontend/views/premium/group-plans/ManageGroupPlan.vue'
          ),
        name: 'manage_group_plan',
        meta: { hideFooter: true },
      },
      {
        path: '/payment_processor/cancel_trial/',
        component: () =>
          import(
            /* webpackChunkName: 'TrialCancellation' */ 'frontend/views/premium/cancellation/TrialCancellation.vue'
          ),
        name: 'payment_trial_cancel_confirmation',
        meta: { hideFooter: true },
      },
      {
        path: '/payment_processor/cancel_trial/reasons_to_stay/',
        component: () =>
          import(
            /* webpackChunkName: 'TrialCancellation' */ 'frontend/views/premium/cancellation/TrialCancellationConfirmation.vue'
          ),
        name: 'payment_trial_cancel_reasons_to_stay',
        meta: { hideFooter: true },
      },
      {
        path: '/payment_processor/cancel_trial_survey/',
        component: () =>
          import(
            /* webpackChunkName: 'TrialCancellation' */ 'frontend/views/premium/cancellation/TrialCancellationSurvey.vue'
          ),
        name: 'payment_trial_cancel_survey',
        meta: { hideFooter: true },
      },
      {
        path: '/payment_processor/cancel_trial_survey/submit/',
        name: 'payment_trial_cancel_survey_submit',
        meta: { redirect: true },
      },
      {
        path: '/payment_processor/cancel_trial_survey/different_plan/',
        component: () =>
          import(
            /* webpackChunkName: 'TrialCancellationDiffPlan' */ 'frontend/views/premium/cancellation/TrialCancellationDifferentPlan.vue'
          ),
        children: [
          {
            path: '',
            component: TrialCancellationPlanSurvey,
            name: 'payment_trial_cancel_different_plan_survey',
            meta: { hideFooter: true },
          },
          {
            path: ':option/',
            component: TrialCancellationPlanStatus,
            name: 'payment_trial_cancel_different_plan_status',
            meta: { hideFooter: true },
          },
        ],
        meta: { hideFooter: true },
      },
      {
        path: '/payment_processor/cancel_trial_survey/different_plan/remind/support/',
        name: 'payment_trial_cancel_diff_plan_send_support_reminder',
        meta: { redirect: true },
      },
      {
        path: '/payment_processor/cancel_trial_survey/different_plan/submit/',
        name: 'payment_trial_cancel_different_plan_survey_submit',
        meta: { redirect: true },
      },
      {
        path: '/payment_processor/cancel_trial_survey/no_surprises/',
        name: 'payment_trial_cancel_no_surprises',
        component: () =>
          import(
            /* webpackChunkName: 'TrialCancellation' */ 'frontend/views/premium/cancellation/TrialCancellationNoSurprises.vue'
          ),
        meta: { hideFooter: true },
      },
      {
        path: '/payment_processor/cancel_trial_survey/term_extension/',
        component: () =>
          import(
            /* webpackChunkName: 'TrialCancellation' */ 'frontend/views/premium/cancellation/TrialCancellationTermExtension.vue'
          ),
        name: 'payment_trial_cancel_term_extension',
        meta: { hideFooter: true },
      },
      {
        path: '/payment_processor/cancel_trial_survey/term_extension/process/',
        name: 'payment_trial_cancel_process_term_extension',
        meta: { redirect: true },
      },
      {
        path: '/payment_processor/cancel_trial_survey/topic_search/',
        name: 'payment_trial_cancel_topic_search',
        component: () =>
          import(
            /* webpackChunkName: 'TrialCancellation' */ 'frontend/views/premium/cancellation/TrialCancellationTopicSearch.vue'
          ),
        meta: { hideFooter: true },
      },
      {
        path: '/payment_processor/suspend_subscription/',
        name: 'payment_processor_suspend_subscription',
        meta: { redirect: true },
      },
      {
        path: '/payment_processor/resume_subscription/',
        name: 'payment_processor_resume_subscription',
        meta: { redirect: true },
      },
      {
        path: '/payment_processor/cancel_trial_complete/',
        component: () =>
          import(
            /* webpackChunkName: 'TrialCancellation' */ 'frontend/views/premium/cancellation/TrialCancellationComplete.vue'
          ),
        name: 'payment_trial_cancel_complete',
        meta: { hideFooter: true },
      },
      {
        path: '/premium/stripe/update-payment-method/',
        component: () =>
          import(
            /* webpackChunkName: 'UpdatePaymentMethod' */ 'frontend/views/premium/UpdatePaymentMethod.vue'
          ),
        name: 'update_payment_method',
        meta: { hideFooter: true },
      },
      {
        path: '/request-refund/',
        name: 'request_refund',
        meta: { redirect: true },
      },
      {
        path: '/saved-for-later/',
        name: 'profile_forlater_component',
        meta: { redirect: true },
      },
      {
        path: '/summer/',
        name: 'summer_landing',
        meta: { redirect: true },
      },
      {
        path: '/summer/approve/:approvalKey/',
        name: 'summer_pass_parent_approval',
        component: () =>
          import(
            /* webpackChunkName: 'SummerPassParentApproval' */ 'frontend/views/summerPass/ParentApproval.vue'
          ),
        meta: {
          hideHeader: true,
          hideHeaderPremiumBanner: true,
          hideReferralBanner: true,
          hideSitewideReferralBanner: true,
          hideHeaderGiftBanner: true,
          hideFooter: true,
        },
      },
      {
        path: '/problems/started/',
        name: 'problems_started_problems',
        meta: { redirect: true },
      },
      {
        path: '/topics/',
        name: 'topics_tab',
        meta: { redirect: true },
      },
      {
        path: '/codex/',
        name: 'codex_page',
        meta: { redirect: true },
      },
      {
        path: '/notifications/',
        name: 'notifications_page',
        meta: { redirect: true },
      },
      {
        path: '/notifications/get_updated_unread_count/',
        name: 'notifications_get_updated_unread_count',
        meta: { redirect: true },
      },
      {
        path: '/give-premium/',
        name: 'gift_purchase_plans',
        component: () =>
          import(
            /* webpackChunkName: 'GiftPaywall' */ 'frontend/views/premium/gifts/paywall/GiftPaywall.vue'
          ),
        meta: {
          hideFooter: true,
        },
      },
      {
        path: '/give-premium/message/:planType?/',
        name: 'gift_purchase_message',
        component: () =>
          import(
            /* webpackChunkName: 'GiftMessageView' */ 'frontend/views/premium/gifts/giftMessage/GiftMessageView.vue'
          ),
        meta: {
          hideFooter: true,
        },
      },
      {
        path: '/give-premium/add_ons/:planType?/',
        name: 'gift_purchase_add_ons',
        component: () =>
          import(
            /* webpackChunkName: 'SubscriptionAddOns' */ 'frontend/views/premium/SubscriptionAddOns.vue'
          ),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/premium/chargebee/checkout/',
        name: 'chargebee_checkout',
        meta: { redirect: true },
      },
      {
        path: '/referral/',
        component: () =>
          import(/* webpackChunkName: 'ReferralHome' */ 'frontend/views/referral/ReferralHome.vue'),
        name: 'referrer_home',
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/referral/invite/',
        name: 'referral_email_invitation',
        meta: { redirect: true },
      },
      {
        path: '/give-premium/success/:purchaseId/',
        name: 'gift_purchase_show',
        meta: { redirect: true },
      },
      {
        path: '/give-premium/chb/success/',
        component: () =>
          import(
            /* webpackChunkName: 'GiftPurchaseReturn' */ 'frontend/views/premium/gifts/ReturnPage.vue'
          ),
        name: 'gift_purchase_success',
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/give-premium/stp/success',
        component: () =>
          import(
            /* webpackChunkName: 'PaymentsGiftsSuccess' */ 'frontend/views/payments/GiftsSuccess.vue'
          ),
        name: 'payments_gifts_success',
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/daily-problems/',
        name: 'daily_problems_listing',
        component: () =>
          import(
            /* webpackChunkName: 'DailyProblemListings' */ 'frontend/views/dailyproblems/DailyProblemListings.vue'
          ),
        meta: {
          hideFooter: true,
        },
      },
      {
        path: '/daily-problems/archives/',
        name: 'daily_problems_archives',
        component: () =>
          import(
            /* webpackChunkName: 'DailyChallengeArchives' */ 'frontend/views/dailyproblems/DailyChallengeArchives.vue'
          ),
        meta: {
          hideFooter: true,
        },
      },
      {
        path: '/daily-problems/:slug/',
        name: 'daily_problems_problem',
        component: () =>
          import(
            /* webpackChunkName: 'DailyChallenge' */ 'frontend/views/dailyproblems/DailyChallenge.vue'
          ),
        meta: { hideFooter: true },
      },
      {
        path: '/courses/',
        component: () =>
          import(
            /* webpackChunkName:'CoursesListing' */ 'frontend/views/courses/CoursesListing.vue'
          ),
        name: 'explorations_menu',
        meta: {
          hideFooter: true,
          showReferralModal: true,
          // This causes a stack overflow when changing the hash when scrolling through the page
          // Disabling until that's fixed
          // useFullPageRequest: true,
        },
      },
      {
        path: '/courses/:courseSlug/',
        component: () =>
          import(
            /* webpackChunkName: 'CoursePageWrapper' */ 'frontend/views/courses/CoursePageWrapper.vue'
          ),
        name: 'explorations_topics',
        meta: {
          hideFooter: true,
          ignoreScrollBehavior: true,
        },
        props: route => ({ fromClassroomId: route.query.fromClassroomId?.toString() }),
      },
      {
        path: '/courses/:courseSlug/landing/',
        component: () =>
          import(
            /* webpackChunkName: 'MarketingLandingPage' */ 'frontend/views/courses/MarketingLandingPage.vue'
          ),
        name: 'course_landing',
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/courses/:courseSlug/:chapterSlug/',
        component: () =>
          import(
            /* webpackChunkName: 'CoursePageWrapper' */ 'frontend/views/courses/CoursePageWrapper.vue'
          ),
        name: 'explorations_chapter',
        meta: {
          hideFooter: true,
        },
      },
      {
        path: '/paths/:learningPathSlug/',
        component: () =>
          import(
            /* webpackChunkName: 'LearningPath' */ 'frontend/views/learningPaths/LearningPath.vue'
          ),
        name: 'learning_path',
        meta: {
          hideFooter: true,
        },
      },
      {
        path: '/landing/sponsor/:slug/',
        name: 'marketing_landing_page',
        component: () =>
          import(
            /* webpackChunkName: 'MarketingLandingView' */ 'frontend/views/landing/marketing/MarketingLandingView.vue'
          ),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/landing/:generalPathSlug/',
        component: () =>
          import(
            /* webpackChunkName: 'GeneralPathLandingPage' */ 'frontend/views/landing/GeneralPathLandingPage.vue'
          ),
        name: 'general_path_landing',
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/mathcounts/',
        component: () =>
          import(
            /* webpackChunkName: 'GeneralPathLandingPage' */ 'frontend/views/landing/GeneralPathLandingPage.vue'
          ),
        name: 'mathcounts_landing',
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/premium/',
        name: 'chargebee_paywall',
        component: () =>
          import(
            /* webpackChunkName: 'PremiumPaywall' */ 'frontend/views/premium/paywall/PremiumPaywall.vue'
          ),
        meta: {
          hideFooter: true,
        },
      },
      {
        path: '/elm_libraries/:name/edit/:version/',
        name: 'library_viewer',
        component: () =>
          import(
            /* webpackChunkName: 'LibraryViewer' */ 'frontend/views/lessons/userLibraries/LibraryViewer.vue'
          ),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/elm_libraries/:name/versions/',
        name: 'library_versions',
        component: () =>
          import(
            /* webpackChunkName: 'LibraryVersionsList' */ 'frontend/views/lessons/userLibraries/LibraryVersionsList.vue'
          ),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/courses/:courseSlug/:chapterSlug/:lessonSlug/versions/',
        name: 'lesson_versions',
        component: () =>
          import(
            /* webpackChunkName: 'LessonVersionsListLoader' */ 'frontend/views/lessons/editor/LessonVersionsListLoader.vue'
          ),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/courses/:courseSlug/:chapterSlug/:lessonSlug/assets/',
        name: 'lesson_assets',
        component: () =>
          import(
            /* webpackChunkName: 'LessonAssetsVersionsList' */ 'frontend/views/lessons/editor/LessonAssetsVersionsList.vue'
          ),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/courses/:courseSlug/:chapterSlug/:lessonSlug/:blockIndex?/',
        name: 'lesson',
        component: () =>
          import(/* webpackChunkName: 'Lesson' */ 'frontend/views/lessons/lesson/Lesson.vue'),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/courses/:courseSlug/:chapterSlug/:lessonSlug/edit/:versionName/',
        name: 'lesson_edit',
        component: () =>
          import(
            /* webpackChunkName: 'LessonEditor' */ 'frontend/views/lessons/editor/LessonEditor.vue'
          ),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/home/',
        name: 'userhome',
        component: () =>
          import(/* webpackChunkName: 'UserHome' */ 'frontend/views/userhome/UserHome.vue'),
        meta: {
          hideFooter: true,
        },
      },
      {
        path: '/premium/',
        name: 'payment_brilliant_square_plans',
        meta: { redirect: true },
      },
      {
        path: '/premium/group-plans/',
        component: () =>
          import(
            /* webpackChunkName: 'GroupPlans' */ 'frontend/views/premium/group-plans/GroupPlans.vue'
          ),
        name: 'group_purchase_front_page',
        meta: {
          hideFooter: true,
        },
      },
      {
        path: '/premium/group-plans/return-page/',
        component: () =>
          import(
            /* webpackChunkName: 'GroupPurchaseReturn' */ 'frontend/views/premium/group-plans/ReturnPage.vue'
          ),
        name: 'premium_group_return_page',
        meta: {
          hideFooter: true,
        },
      },
      {
        path: '/premium/group-plans/redeem-seat/',
        component: () =>
          import(
            /* webpackChunkName: 'RedeemSeat' */ 'frontend/views/premium/group-plans/RedeemSeat.vue'
          ),
        name: 'premium_group_redeem_seat_page',
        meta: {
          hideFooter: true,
        },
      },
      {
        path: '/premium/success/',
        component: () =>
          import(
            /* webpackChunkName: 'PremiumSuccess' */ 'frontend/views/premium/success/PremiumSuccess.vue'
          ),
        name: 'payment_brilliant_premium_success',
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/principles/',
        name: 'learning_manifesto',
        meta: { redirect: true },
      },
      {
        path: '/about/',
        name: 'about',
        component: About,
        meta: {
          hideHeader: true,
          hideFooter: true,
          useFullPageRequest: true,
        },
      },
      {
        path: '/roundabout/',
        name: 'roundabout',
        component: About,
        meta: {
          hideHeader: true,
          hideFooter: true,
          useFullPageRequest: true,
        },
      },
      {
        path: '/careers/',
        component: () =>
          import(/* webpackChunkName: 'Careers' */ 'frontend/views/company/Careers.vue'),
        name: 'careers_page',
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/art-and-design/',
        name: 'art_and_design',
        component: () =>
          import(
            /* webpackChunkName: 'ArtDesignPage' */ 'frontend/views/flatpages/art-and-design/ArtDesignPage.vue'
          ),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/search/',
        name: 'search',
        meta: { redirect: true },
      },
      {
        path: '/testimonials/',
        component: () =>
          import(/* webpackChunkName: 'Testimonials' */ 'frontend/views/company/Testimonials.vue'),
        name: 'testimonials',
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/terms-of-use/',
        name: 'flatpage_termsofuse',
        meta: { redirect: true },
      },
      {
        path: '/privacy/',
        component: () =>
          import(
            /* webpackChunkName: 'PrivacyPolicy' */ 'frontend/views/flatpages/PrivacyPolicy.vue'
          ),
        name: 'flatpage_privacypolicy',
      },
      {
        path: '/educators-privacy/',
        component: () =>
          import(
            /* webpackChunkName: 'EducatorsPrivacyPolicy' */ 'frontend/views/flatpages/PrivacyPolicyEducators.vue'
          ),
        name: 'flatpage_privacypolicy_educators',
      },
      {
        path: '/educators-terms-of-use/',
        component: () =>
          import(
            /* webpackChunkName: 'EducatorsTermsOfUse' */ 'frontend/views/flatpages/TermsOfUseEducators.vue'
          ),
        name: 'flatpage_termsofuse_educators',
      },
      {
        path: '/privacy/05-21-2018/',
        component: () =>
          import(
            /* webpackChunkName: 'PrivacyPolicy2018' */ 'frontend/views/flatpages/PrivacyPolicy2018.vue'
          ),
        name: 'flatpage_privacypolicy_05_21_2018',
      },
      {
        path: '/privacy/03-24-2014/',
        component: () =>
          import(
            /* webpackChunkName: 'PrivacyPolicy2014' */ 'frontend/views/flatpages/PrivacyPolicy2014.vue'
          ),
        name: 'flatpage_privacypolicy_03_24_2014',
      },
      {
        path: '/cookie-policy/',
        component: () =>
          import(
            /* webpackChunkName: 'CookiePolicy' */ 'frontend/views/flatpages/CookiePolicy.vue'
          ),
        name: 'flatpage_cookiepolicy',
      },
      {
        path: '/ccpa-policy/',
        component: () =>
          import(/* webpackChunkName: 'CCPAPolicy' */ 'frontend/views/flatpages/CCPAPolicy.vue'),
        name: 'flatpage_ccpa_policy',
      },
      {
        path: '/community-faq/',
        component: () =>
          import(
            /* webpackChunkName: 'CommunityFAQ' */ 'frontend/views/flatpages/CommunityFAQ.vue'
          ),
        name: 'flatpage_community_faq',
      },
      {
        path: '/experiments_framework/dashboard/',
        component: () =>
          import(
            /* webpackChunkName: 'ExperimentsDashboard' */ 'frontend/views/admin/experiments/ExperimentsDashboard.vue'
          ),
        name: 'experiments_dashboard',
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/payment_processor/wadtudr-actions/:userID/',
        component: () =>
          import(
            /* webpackChunkName: 'WadtudrActions' */ 'frontend/views/admin/WadtudrActions.vue'
          ),
        name: 'wadtudr_actions',
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/payment_processor/admin/plan-filters/',
        component: () =>
          import(
            /* webpackChunkName: 'PlanFiltersAdmin' */ 'frontend/views/premium/admin/PlanFilters.vue'
          ),
        name: 'plan_filters',
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/classroom/',
        component: () =>
          import(
            /* webpackChunkName: 'ClassroomLanding' */ 'frontend/views/classrooms/ClassroomLanding.vue'
          ),
        name: 'classroom',
        meta: {
          hideFooter: true,
        },
      },
      // following route not internally referenced, just added for sanity and for compatibility to routes existed
      // in the backend
      {
        path: '/classroom/:classroomId/',
        component: () =>
          import(
            /* webpackChunkName: 'ClassroomLanding' */ 'frontend/views/classrooms/ClassroomLanding.vue'
          ),
        name: 'classroom_by_id',
        meta: {
          hideFooter: true,
        },
      },
      {
        path: '/classroom/:classroomId/courses/',
        component: () =>
          import(
            /* webpackChunkName: 'ClassroomLanding' */ 'frontend/views/classrooms/ClassroomLanding.vue'
          ),
        name: 'classroom_course_list',
        meta: {
          hideFooter: true,
        },
      },
      {
        path: '/classroom/:classroomId/class-progress/',
        component: () =>
          import(
            /* webpackChunkName: 'ClassroomClassProgress' */ 'frontend/views/classrooms/ClassroomClassProgress.vue'
          ),
        name: 'classroom_class_progress',
        meta: {
          hideFooter: true,
        },
      },
      {
        path: '/classroom/:classroomId/course-progress/',
        component: () =>
          import(
            /* webpackChunkName: 'ClassroomCourseProgress' */ 'frontend/views/classrooms/ClassroomCourseProgress.vue'
          ),
        name: 'classroom_course_progress',
        meta: {
          hideFooter: true,
        },
      },
      {
        path: '/classroom/:classroomId/student/:studentId/',
        component: () =>
          import(
            /* webpackChunkName: 'StudentCourseProgress' */ 'frontend/views/classrooms/StudentCourseProgress.vue'
          ),
        name: 'classroom_by_student',
        meta: {
          hideFooter: true,
        },
      },
      {
        path: '/classroom/:classroomId/daily-challenges/',
        component: () =>
          import(
            /* webpackChunkName: 'ClassroomsDCs' */ 'frontend/views/classrooms/ClassroomDailyChallenges.vue'
          ),
        name: 'classroom_dc_view',
        meta: {
          hideFooter: true,
        },
      },
      {
        path: '/classroom/:classroomId/student/:studentId/daily-challenges/',
        component: () =>
          import(
            /* webpackChunkName: 'ClassroomsDCsStudent' */ 'frontend/views/classrooms/StudentDailyChallengeProgress.vue'
          ),
        name: 'classroom_dc_by_student',
        meta: {
          hideFooter: true,
        },
      },
      {
        path: '/classroom/:classroomId/students/',
        component: () =>
          import(
            /* webpackChunkName: 'ClassroomsStudents' */ 'frontend/views/classrooms/StudentList.vue'
          ),
        name: 'classroom_student_list',
        meta: {
          hideFooter: true,
        },
      },
      {
        path: '/educators/',
        component: () =>
          import(
            /* webpackChunkName: 'EducatorsLanding' */ 'frontend/views/classrooms/TeacherLanding.vue'
          ),
        name: 'teacher_landing',
        meta: {
          hideFooter: true,
          educatorsHome: true,
          hideHeaderSwitch: 'enable_savvy_educators_landingpage',
        },
      },
      {
        path: '/educators/apply/',
        component: () =>
          import(
            /* webpackChunkName: 'EducatorsApplication' */ 'frontend/views/classrooms/TeacherApplication.vue'
          ),
        name: 'teacher_application',
        meta: {
          hideFooter: true,
        },
      },
      {
        path: '/help/',
        name: 'flatpage_faq',
        meta: { redirect: true },
      },
      {
        path: '/moderators/',
        name: 'moderators_dashboard',
        meta: { redirect: true },
      },
      {
        path: '/utils/create_test_user/',
        name: 'staff_create_test_user',
        meta: { redirect: true },
      },
      {
        path: '/testing/sprites/',
        component: () =>
          import(
            /* webpackChunkName: 'TestingSprites' */ 'frontend/views/testing/TestingSprites.vue'
          ),
        name: 'testing_sprites',
      },
      {
        path: '/latex-image-export/',
        component: () =>
          import(
            /* webpackChunkName: 'LatexImageExport' */ 'frontend/views/utils/LatexImageExport.vue'
          ),
        name: 'latex_image_export',
      },
      {
        path: '/s/:path(.*)*',
        name: 'savvy_page',
        component: () =>
          import(/* webpackChunkName: 'SavvyPage' */ 'frontend/views/savvy/SavvyPage.vue'),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/welcome/',
        name: 'nux_welcome',
        component: () =>
          import(/* webpackChunkName: 'UserWelcome' */ 'frontend/views/nux/UserWelcome.vue'),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/welcome/right-place/',
        name: 'nux_welcome_product_reason_one',
        component: () =>
          import(
            /* webpackChunkName: 'ProductReasonRightPlace' */ 'frontend/views/nux/ProductReasonRightPlace.vue'
          ),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/welcome/math-comfort/',
        name: 'nux_welcome_math_comfort',
        component: () =>
          import(
            /* webpackChunkName: 'NuxMathComfortSurvey' */ 'frontend/views/nux/NuxMathComfortSurvey.vue'
          ),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/welcome/try/',
        name: 'nux_welcome_try',
        component: () =>
          import(/* webpackChunkName: 'NuxTrySurvey' */ 'frontend/views/nux/NuxTrySurvey.vue'),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/welcome/career/',
        name: 'nux_welcome_career',
        component: () =>
          import(
            /* webpackChunkName: 'NuxCareerSurvey' */ 'frontend/views/nux/NuxCareerSurvey.vue'
          ),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/welcome/school-goal/',
        name: 'nux_welcome_academic',
        component: () =>
          import(
            /* webpackChunkName: 'NuxAcademicSurvey' */ 'frontend/views/nux/NuxAcademicSurvey.vue'
          ),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/welcome/active-learning/',
        name: 'nux_welcome_product_reason_two',
        component: () =>
          import(
            /* webpackChunkName: 'ProductReasonActiveLearning' */ 'frontend/views/nux/ProductReasonActiveLearning.vue'
          ),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/welcome/time-goal/',
        name: 'nux_welcome_time_goal',
        component: () =>
          import(/* webpackChunkName: 'TimeGoal' */ 'frontend/views/nux/TimeGoal.vue'),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/welcome/day-goal/',
        name: 'nux_welcome_day_goal',
        component: () => import(/* webpackChunkName: 'DayGoal' */ 'frontend/views/nux/DayGoal.vue'),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/welcome/testimonial/',
        name: 'nux_welcome_testimonial',
        component: () =>
          import(/* webpackChunkName: 'NuxTestimonial' */ 'frontend/views/nux/NuxTestimonial.vue'),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/login/',
        name: 'login_nux',
        component: () =>
          import(/* webpackChunkName: 'NuxSignupLogin' */ 'frontend/views/nux/NuxLogin.vue'),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/signup/',
        name: 'signup_nux',
        component: () =>
          import(/* webpackChunkName: 'NuxSignupLogin' */ 'frontend/views/nux/NuxSignup.vue'),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/welcome/where-to-start/',
        name: 'nux_welcome_where_to_start',
        component: () =>
          import(
            /* webpackChunkName: 'NuxWhereToStartSurvey' */ 'frontend/views/nux/NuxWhereToStartSurvey.vue'
          ),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/welcome/learning-path/:learningPathSlug/',
        name: 'nux_learning_path',
        component: () =>
          import(
            /* webpackChunkName: 'NuxLearningPath' */ 'frontend/views/nux/NuxLearningPath.vue'
          ),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/algebra/',
        name: 'algebra',
        meta: { redirect: true },
      },
      {
        path: '/geometry/',
        name: 'geometry',
        meta: { redirect: true },
      },
      {
        path: '/number-theory/',
        name: 'number-theory',
        meta: { redirect: true },
      },
      {
        path: '/calculus/',
        name: 'calculus',
        meta: { redirect: true },
      },
      {
        path: '/discrete-mathematics/',
        name: 'discrete-mathematics',
        meta: { redirect: true },
      },
      {
        path: '/basic-mathematics/',
        name: 'basic-mathematics',
        meta: { redirect: true },
      },
      {
        path: '/logic/',
        name: 'logic',
        meta: { redirect: true },
      },
      {
        path: '/mechanics/',
        name: 'mechanics',
        meta: { redirect: true },
      },
      {
        path: '/electricity-and-magnetism/',
        name: 'electricity-and-magnetism',
        meta: { redirect: true },
      },
      {
        path: '/computer-science/',
        name: 'computer-science',
        meta: { redirect: true },
      },
      {
        path: '/quantitative-finance/',
        name: 'quantitative-finance',
        meta: { redirect: true },
      },
      {
        path: '/chemistry/',
        name: 'chemistry',
        meta: { redirect: true },
      },
      {
        path: '/payment_processor/cancel-premium/:subscriptionId/',
        name: 'payment_subscription_cancel_confirmation',
        component: () =>
          import(
            /* webpackChunkName: 'PremiumCancellation' */ 'frontend/views/premium/cancellation/PremiumCancellation.vue'
          ),
        meta: {
          hideHeader: false,
          hideFooter: true,
        },
      },
      {
        path: '/wsj/',
        name: 'wsj_landing',
        component: () =>
          import(
            /* webpackChunkName: 'WSJLandingPage' */ 'frontend/views/flatpages/wsj-landing/WSJLandingPage.vue'
          ),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/authors/home/',
        name: 'authors_dashboard',
        component: () =>
          import(
            /* webpackChunkName: 'AuthorsDashboard' */ 'frontend/views/authors/AuthorsDashboard.vue'
          ),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/authors/examples/diagrammar/',
        name: 'authors_diagrammar_examples',
        component: () =>
          import(
            /* webpackChunkName: 'AuthorsDiagrammarExamples' */ 'frontend/views/authors/AuthorsDiagrammarExamples.vue'
          ),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/authors/courses/:courseSlug/edit/:versionName/',
        name: 'course_edit',
        component: () =>
          import(/* webpackChunkName: 'CourseEdit' */ 'frontend/views/authors/CourseEdit.vue'),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/authors/courses/:courseSlug/versions/',
        name: 'course_versions',
        component: () =>
          import(
            /* webpackChunkName: 'CourseVersions' */ 'frontend/views/authors/CourseVersions.vue'
          ),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        path: '/accept-gift-subscription/:code/',
        name: 'gift_purchase_accept',
        component: () =>
          import(
            /* webpackChunkName: 'GiftPurchaseAccept' */ 'frontend/views/premium/gifts/giftPurchaseAccept/GiftPurchaseAccept.vue'
          ),
        meta: {
          hideFooter: true,
        },
      },
      {
        path: '/levels/:learningPathSlug/',
        name: 'learning_path_v2',
        component: () =>
          import(
            /* webpackChunkName: 'LearningPathV2' */ 'frontend/views/learningPathV2/LearningPath.vue'
          ),
        meta: {
          hideFooter: true,
          hideHeaderParam: { key: 'from', value: 'signup' },
        },
      },
    ] as RouteConfig[]),

    scrollBehavior(to, from, savedPosition) {
      if (to.meta.ignoreScrollBehavior) return;

      if (to.hash) {
        const targetElement = document.getElementById(to.hash.replace('#', ''));
        if (targetElement) {
          window.scroll(0, targetElement.offsetTop);
          return { selector: to.hash };
        }
      }

      if (to.path !== from.path) {
        // TODO figure out how to wait until the page is rendered before updating the position
        // if (savedPosition) {
        //   return savedPosition;
        // }

        return { x: 0, y: 0 };
      }

      return;
    },
  });

  let isInitialRoute = true;

  router.beforeEach((to: Route, from: Route, next) => {
    const serverSideContext = getServerSideContext();

    // Calling this will stop routing and do a hard nav to the incoming route
    const doFullPageRequest = (path = to.fullPath) => {
      next(false);
      getWindowLocation().href = path;
    };

    // Check if a page is flagged for a full page request. (On soft navigation only!)
    if (!serverSideContext && !isInitialRoute && to.meta.useFullPageRequest === true) {
      return doFullPageRequest();
    }
    isInitialRoute = false;

    // get featureSwitches for CSR or SSR
    let featureSwitches;
    let inExperiment;
    let globalState;
    const hasGlobalState = Boolean(
      router.app && router.app.$store && router.app.$store.state && router.app.$store.state.globals,
    );
    if (hasGlobalState) {
      globalState = router.app.$store.state.globals;
      featureSwitches = globalState.feature_switches;
      if (globalState.experiments) {
        inExperiment = router.app.$store.getters['globals/inExperiment'];
      }
    }
    if (serverSideContext && serverSideContext.backend && serverSideContext.backend.globals) {
      globalState = serverSideContext.backend.globals;
      if (featureSwitches === undefined) {
        featureSwitches = globalState.feature_switches;
      }
      if (inExperiment === undefined) {
        inExperiment = inExperimentGetter(globalState.experiments);
      }
    }
    // Guard feature switches
    if (to.meta.featureSwitches && featureSwitches) {
      for (const featureSwitch of to.meta.featureSwitches) {
        if (featureSwitches && !featureSwitches[featureSwitch]) {
          if (serverSideContext) {
            // tslint:disable-next-line:no-console
            console.log(
              `${featureSwitch} from router is not in feature_switches or is set to false`,
            );
          } else {
            return doFullPageRequest();
          }
        }
      }
    }
    // Guard experiments
    if (to.meta.experiments && inExperiment) {
      for (const experiment of to.meta.experiments) {
        if (to.meta.experimentTreatments) {
          let inTreatment = false;
          for (const expTreatment of to.meta.experimentTreatments) {
            if (inExperiment(experiment, expTreatment)) {
              inTreatment = true;
            }
          }
          if (!inTreatment) {
            if (serverSideContext) {
              // tslint:disable-next-line:no-console
              console.log(`${experiment} from router is not in experiments or is set to false`);
            } else {
              return doFullPageRequest();
            }
          }
        } else if (!inExperiment(experiment, 'experiment')) {
          if (serverSideContext) {
            // tslint:disable-next-line:no-console
            console.log(`${experiment} from router is not in in experiments or is set to false`);
          } else {
            return doFullPageRequest();
          }
        }
      }
    }

    // implicity redirect if route doesn't have a match
    if (to.matched.length === 0) {
      if (serverSideContext) {
        // tslint:disable-next-line:no-console
        console.log(`router doesn't have a match for path ${to.fullPath}`);
      } else {
        let href: string;
        if (to.fullPath.indexOf('http') !== -1) {
          href = `http${to.fullPath.split('http')[1]}`;
        } else {
          href = to.fullPath;
        }
        return doFullPageRequest(href);
      }
    }
    if (to.meta) {
      if (to.meta.redirect) {
        if (serverSideContext) {
          // tslint:disable-next-line:no-console
          console.log(`${to.fullPath} is is trying to redirect`);
        } else {
          return doFullPageRequest();
        }
      }
    }

    if (router.app && router.app.$store) {
      if (from?.name !== to?.name) {
        // do not trigger for route params changes, (eg  course chapter change in lesson view)
        router.app.$store.dispatch('banner/prefetchBanner', {
          routePath: to.path,
          q: to.query,
        });
      }
    }
    next();
  });

  return router;
}

/** API Router -- returns a Vue router with only API routes */
export function apiRouter(): Router {
  return new Router({
    mode: 'history',
    routes: updateConfig([
      {
        path: '/courses/:courseSlug/:chapterSlug/:lessonSlug/create/',
        name: 'lesson_create',
      },
      {
        path: '/authors/courses/create/',
        name: 'course_create',
      },
      {
        path: '/authors/courses/create_version/',
        name: 'course_create_version',
      },
      {
        path: '/authors/courses/change-state/',
        name: 'course_change_state',
      },
      {
        path: '/courses/:courseSlug/:chapterSlug/:lessonSlug/autosave/create/',
        name: 'lesson_autosave_create',
      },
      {
        path: '/courses/:courseSlug/:chapterSlug/:lessonSlug/autosave/list/',
        name: 'lesson_autosave_list',
      },
      {
        path: '/courses/:courseSlug/:chapterSlug/:lessonSlug/autosave/load/',
        name: 'lesson_autosave_load',
      },
      {
        path: '/courses/:courseSlug/:chapterSlug/:lessonSlug/draft/create/',
        name: 'lesson_draft_create',
      },
      {
        path: '/courses/:courseSlug/:chapterSlug/:lessonSlug/draft/create-empty/',
        name: 'lesson_draft_create_empty',
      },
      {
        path: '/courses/:courseSlug/:chapterSlug/:lessonSlug/draft/update/',
        name: 'lesson_draft_update',
      },
      {
        path: '/courses/:courseSlug/:chapterSlug/:lessonSlug/draft/publish/',
        name: 'lesson_draft_publish',
      },
      {
        path: '/courses/:courseSlug/:chapterSlug/:lessonSlug/versions/',
        name: 'lesson_versions',
      },
      {
        path: '/courses/:courseSlug/:chapterSlug/:lessonSlug/history/',
        name: 'lesson_edit_history',
      },
      {
        path: '/courses/admin/upload/',
        name: 'lesson_upload_assets',
      },
      {
        path: '/courses/:courseSlug/:chapterSlug/:lessonSlug/endstate-context/',
        name: 'lesson_endstate_context',
      },
      {
        path: '/courses/:courseSlug/:chapterSlug/:lessonSlug/report/load/',
        name: 'lesson_reports',
      },
      {
        path: '/courses/:courseSlug/:chapterSlug/:lessonSlug/feedback/load/',
        name: 'lesson_feedback_load',
      },
      {
        path: '/courses/:courseSlug/reset-user-state/',
        name: 'course_userstate_reset',
      },
      {
        path: '/courses/:courseSlug/bulk-reset-user-state/',
        name: 'bulk_course_userstate_reset',
      },
      {
        path: '/api/v1/thumbs_feedback/',
        name: 'api_submit_feedback',
      },
      {
        path: '/courses/:courseSlug/:chapterSlug/:lessonSlug/userstate/reset/',
        name: 'lesson_userstate_reset',
      },
      {
        path: '/courses/:courseSlug/:chapterSlug/:lessonSlug/userstate/save/',
        name: 'lesson_userstate_save',
      },
      {
        path: '/courses/:courseSlug/:chapterSlug/:lessonSlug/report/',
        name: 'lesson_report_problem',
      },
      {
        path: '/courses/:courseSlug/:chapterSlug/:lessonSlug/retire/',
        name: 'lesson_retire_version',
      },
      {
        path: '/courses/api/:courseSlug/:chapterSlug/:lessonSlug/versions/',
        name: 'lesson_versions_api',
      },
      {
        path: '/courses/api/lesson/bulk-retire/',
        name: 'lesson_bulk_retire_version',
      },
      {
        path: '/authors/packages/compile/',
        name: 'elm_compile_with_packages',
      },
      {
        path: '/authors/packages/list/',
        name: 'elm_packages_list',
      },
      {
        path: '/api/v1/figma-import/',
        name: 'figma_import_start',
      },
      {
        path: '/api/v1/figma-import/status/',
        name: 'figma_import_status',
      },
      {
        path: '/sounds/sound-actions/',
        name: 'sound_actions',
      },
      {
        path: '/lottie-animations/',
        name: 'lottie_animations',
      },
      {
        path: '/give-premium/message/save/',
        name: 'gift_purchase_message_save',
      },
      {
        path: '/give-premium/activate/',
        name: 'gift_purchase_activate_api',
      },
      {
        path: '/authors/courses/update/',
        name: 'course_update_content',
      },
      {
        path: '/authors/courses/lesson-search/',
        name: 'course_lesson_search',
      },
      {
        path: '/courses/lesson-owner/details/',
        name: 'lesson_owner_details',
      },
      {
        path: '/courses/lesson-owner/take-ownership/',
        name: 'lesson_owner_take_ownership',
      },
      {
        path: '/api/v1/daily-problems/actions/:dcSlug/',
        name: 'api_dailyproblems_actions',
      },
      {
        path: '/api/v1/daily-problems/archives/',
        name: 'api_dailyproblems_archives',
      },
      {
        path: '/api/v1/daily-problems/',
        name: 'api_dailyproblems_base',
      },
      {
        path: '/api/v4/leagues/current/',
        name: 'current_league',
      },
      {
        path: '/api/v4/leagues/viewed_endstate/',
        name: 'leagues_viewed_endstate',
      },
      {
        path: '/api/v4/leagues/welcome_to_leagues_dismissed/',
        name: 'welcome_to_leagues_dismissed',
      },
      {
        path: '/api/v3/codex/execute/',
        name: 'codex_execute',
      },
      {
        path: '/api/v3/codex/check/',
        name: 'codex_check',
      },
    ]),
  });
}

/** Resolves an API route. Throws an error if the route doesn't exist. */
export function apiResolve(loc: RawLocation): { href: string; route: Route } {
  const response = apiRouter().resolve(loc);
  if (response.route.matched.length === 0) {
    if (typeof loc === 'string') {
      throw new Error(`Unable to resolve raw API location '${loc}'`);
    } else {
      throw new Error(`Unable to resolve API location named '${loc.name}'`);
    }
  }
  return response;
}

export const ROUTER_SKIP_FETCH_DATA_PROPERTY = '_skipFetchData';

/** Navigate to a new location without fetching page-level data for the new page */
export function pushLocationWithoutFetch(router: Router, to: RawLocation, callback: () => void) {
  (router as any)[ROUTER_SKIP_FETCH_DATA_PROPERTY] = true;
  router.push(to, callback);
}

/** Update the current location without fetching page-level data for the new page */
export function replaceLocationWithoutFetch(router: Router, to: RawLocation, callback: () => void) {
  (router as any)[ROUTER_SKIP_FETCH_DATA_PROPERTY] = true;
  router.replace(to, callback);
}
